import { resolveOption } from 'unitData/options/optionResolution';
import { store } from 'createStore';
import {
  CONVERSIONS,
  convertToCurrentMetric,
  getMetricUnit,
} from 'util/conversions';
import type { UnitInfoTextInterface } from '.';
import { UnitNames, UnitTypes } from 'unitData/types';
import { BreakerApplication } from 'unitData/Breaker/BreakerTypes';
import type {
  AttachementTool,
  DisplayBaseInterface,
  PortOutputFlowInterface,
  UnitInfoBananaScreenInterface,
  UnitInfoBaseInterface,
  UnitInfoCMScreenInterface,
  UnitInfoCrusherInterface,
  UnitInfoCrusherVSIInterface,
  UnitInfoFreeFallScreenSFInterface,
  UnitInfoFreeFallScreenSSInterface,
  UnitInfoGenericCrusherInterface,
  UnitInfoGenericScreenInterface,
  UnitInfoGrizzlyFeederInterface,
  UnitInfoGrizzlyScreenSGInterface,
  UnitInfoGyratoryCrusherCGInterface,
  UnitInfoImpactCrusherCIInterface,
  UnitInfoJawCrusherInterface,
  UnitInfoKwataniScreenInterface,
  UnitInfoLMScreenInterface,
  UnitInfoMobileConeInterface,
  UnitInfoMobileHSIInterface,
  UnitInfoMobileJawAndConeInterface,
  UnitInfoMobileJawInterface,
  UnitInfoMobileScreenInterface,
  UnitInfoMobileVSIInterface,
  UnitInfoMultislopeScreenInterface,
  UnitInfoPanFeederInterface,
  UnitInfoPlateFeederInterface,
  UnitInfoSchenckScreenInterface,
  UnitInfoStorageInterface,
} from 'store/units/types';
import { MetricTypes } from 'store/settings/types';
import type { SimulationResponseUnitInfoInterface } from 'store/simulation/types';
import type { MotionScreenContext } from './deckDisplayConfigs';
import {
  motionScreenDeckConfig,
  multislopeScreenDeckConfig,
} from './deckDisplayConfigs';
import { deckDisplayFromConfig } from './deckDisplayHelpers';

export const toMetric =
  (conversion: keyof typeof CONVERSIONS) =>
  (
    text: string,
    value: number | undefined,
    options?: { decimals?: number; hideUnit?: boolean },
  ) =>
    `${text} ${convertToCurrentMetric({
      value,
      metricType: store.getState().settingsState.metricType,
      conversionType: CONVERSIONS[conversion],
      decimals: options?.decimals,
    })} ${
      !options?.hideUnit
        ? getMetricUnit(
            store.getState().settingsState.metricType,
            CONVERSIONS[conversion],
          )
        : ''
    }`;

export const toMetricCapacity = toMetric('CAPACITY');
export const toMetricMM = toMetric('MILLIMETER');
export const toMetricRoundConv = toMetric('MILLIMETERNOROUNDCONV');
export const toMetricDensity = toMetric('DENSITY');

const showEquipmentType = () =>
  store.getState().settingsState?.unitDisplay?.showEquipmentType;

const showLoad = () => store.getState().settingsState?.unitDisplay?.showLoad;
export const getMetricType = () => store.getState().settingsState.metricType;
export const isImperial = () => getMetricType().value === 'IMPERIAL';

export const addValue =
  (textArray: UnitInfoTextInterface[]) =>
  (text: string, display: boolean, title?: boolean) => {
    if (display) {
      textArray.push({ text, title });
    }
  };

export const unitName = (
  type: UnitTypes,
  unitInfo:
    | DisplayBaseInterface<Record<string, unknown>>
    | UnitInfoBananaScreenInterface,
  textArray: UnitInfoTextInterface[],
) => {
  const add = addValue(textArray);
  const found = UnitNames.find((u) => u.type === type);
  const name = found?.name2 ?? found?.name ?? type;
  const display = unitInfo.display.displayUnitName && showEquipmentType();
  add(name, display, true);
};

export const storageUnitName = (
  unitInfo: UnitInfoStorageInterface,
  types: { id: number; displayName: string; image: string }[],
  textArray: UnitInfoTextInterface[],
) => {
  const name = resolveOption(types, unitInfo.storageImg).label;
  if (!name) {
    return;
  }
  const display = unitInfo.display.displayUnitName && showEquipmentType();
  addValue(textArray)(name, display, true);
};

export const modelName = (
  unitInfo: UnitInfoBaseInterface &
    DisplayBaseInterface<Record<string, unknown>>,
  textArray: UnitInfoTextInterface[],
  label = unitInfo.modelName,
) => {
  addValue(textArray)(label, unitInfo.display.displayModelName, true);
};

export const mobileUnitName = (
  unitInfo:
    | UnitInfoMobileScreenInterface
    | UnitInfoMobileHSIInterface
    | UnitInfoMobileVSIInterface
    | UnitInfoMobileJawInterface
    | UnitInfoMobileConeInterface
    | UnitInfoMobileJawAndConeInterface,
  text: string,
  textArray: UnitInfoTextInterface[],
) => {
  addValue(textArray)(
    text,
    unitInfo.display.displayUnitName && showEquipmentType(),
    true,
  );
};

export const nbrOfDecks = (
  unitInfo:
    | UnitInfoCMScreenInterface
    | UnitInfoLMScreenInterface
    | UnitInfoGrizzlyScreenSGInterface
    | UnitInfoFreeFallScreenSSInterface
    | UnitInfoFreeFallScreenSFInterface
    | UnitInfoMultislopeScreenInterface
    | UnitInfoGenericScreenInterface,
  textArray: UnitInfoTextInterface[],
) => {
  addValue(textArray)(
    `Decks: ${unitInfo.nbrOfDecks}`,
    unitInfo.display.displayNumberOfDecks,
  );
};

export const wetScreening = (
  unitInfo:
    | UnitInfoCMScreenInterface
    | UnitInfoBananaScreenInterface
    | UnitInfoSchenckScreenInterface
    | UnitInfoKwataniScreenInterface
    | UnitInfoLMScreenInterface
    | UnitInfoMultislopeScreenInterface,
  textArray: UnitInfoTextInterface[],
) => {
  addValue(textArray)(
    `Type of screening: ${unitInfo.wetScreening ? 'Wet' : 'Dry'}`,
    unitInfo.display.displayWetScreening,
  );
};

export const inclination = (
  unitInfo: {
    incline?: number;
    inclination?: number;
    display: { displayInclination: boolean };
  },
  textArray: UnitInfoTextInterface[],
) => {
  addValue(textArray)(
    `Inclination: ${unitInfo.incline ?? unitInfo.inclination ?? 'N/A'} deg`,
    unitInfo.display.displayInclination,
  );
};

export const screenDeckDisplay = (
  index: number,
  textArray: UnitInfoTextInterface[],
  deckProperties: Array<{ text: string; display: boolean }>,
  deckName?: string,
) => {
  const add = addValue(textArray);

  const shouldDisplay = deckProperties.some((prop) => prop.display);

  if (!shouldDisplay) {
    return;
  }

  add(deckName ?? `Deck ${index + 1}`, true);

  deckProperties.forEach(({ text, display }) => {
    add(`\u200B    ${text}`, display);
  });
};

export const decksDisplay = (
  type: UnitTypes,
  unitInfo:
    | UnitInfoCMScreenInterface
    | UnitInfoLMScreenInterface
    | UnitInfoMultislopeScreenInterface,
  simunitinfo: SimulationResponseUnitInfoInterface,
  textArray: UnitInfoTextInterface[],
) => {
  Object.values(unitInfo.deckList).forEach((deck, index) => {
    if (index < unitInfo.nbrOfDecks) {
      if (type === UnitTypes.MULTISLOPE_SCREEN) {
        deckDisplayFromConfig(
          deck,
          multislopeScreenDeckConfig.properties,
          index,
          textArray,
        );
      } else {
        const context: MotionScreenContext = {
          screenCalcMethod:
            store.getState().settingsState?.screenCalcMethod.value,
          simunitInfo: simunitinfo,
          deckIndex: index,
        };
        const properties = motionScreenDeckConfig.getProperties(context);
        deckDisplayFromConfig(deck, properties, index, textArray);
      }
    }
  });
};

export const loadDisplay = (
  unitInfo: { display: { displayLoad: boolean } },
  load: number | undefined,
  textArray: UnitInfoTextInterface[],
  round?: boolean,
) => {
  const display = unitInfo.display.displayLoad && showLoad();
  if (typeof load !== 'number') {
    return;
  }

  addValue(textArray)(`Load: ${round ? Math.round(load) : load} %`, display);
};

export const loadDisplayMobiles = (
  unitInfo: { display: { displayLoad: boolean } },
  feederLoad: number | undefined,
  crusherLoad: number | undefined,
  screenLoad: number | undefined,
  textArray: UnitInfoTextInterface[],
  round?: boolean,
) => {
  const display = unitInfo.display.displayLoad && showLoad();
  if (typeof feederLoad === 'number' && feederLoad > 0) {
    addValue(textArray)(
      `Feeder load: ${round ? Math.round(feederLoad) : feederLoad} %`,
      display,
    );
  }
  if (typeof crusherLoad === 'number' && crusherLoad > 0) {
    addValue(textArray)(
      `Crusher load: ${round ? Math.round(crusherLoad) : crusherLoad} %`,
      display,
    );
  }
  if (typeof screenLoad === 'number' && screenLoad > 0) {
    addValue(textArray)(
      `Screen load: ${round ? Math.round(screenLoad) : screenLoad} %`,
      display,
    );
  }
};

export const description = (
  unitInfo: UnitInfoGenericScreenInterface | UnitInfoGenericCrusherInterface,
  textArray: UnitInfoTextInterface[],
) => {
  if (typeof unitInfo.otherModelName === 'undefined') {
    return;
  }

  addValue(textArray)(
    unitInfo.otherModelName,
    unitInfo.display.displayDescription,
    true,
  );
};

export const customModel = (
  unitInfo:
    | UnitInfoCrusherInterface
    | UnitInfoImpactCrusherCIInterface
    | UnitInfoJawCrusherInterface
    | UnitInfoCrusherVSIInterface
    | UnitInfoGyratoryCrusherCGInterface,
  modelName: string,
  textArray: UnitInfoTextInterface[],
) => {
  const add = addValue(textArray);
  if (unitInfo.showCustomModel) {
    if (typeof unitInfo.otherModelName !== 'undefined') {
      add(unitInfo.otherModelName, true, true);
    }
    add(`Calculated as ${modelName}`, true);
  } else if (modelName) {
    add(modelName, true, true);
  }
};

export const feederModelName = (
  unitInfo:
    | UnitInfoPlateFeederInterface
    | UnitInfoGrizzlyFeederInterface
    | UnitInfoPanFeederInterface,
  textArray: UnitInfoTextInterface[],
  modelName?: string,
) => {
  const add = addValue(textArray);
  const { otherModel, display, otherModelName } = unitInfo;

  if (otherModel) {
    add(
      typeof otherModelName === 'undefined' ? '' : otherModelName,
      otherModel && display.displayModelName,
      true,
    );
  } else {
    add(`${modelName ?? unitInfo.modelName}`, display.displayModelName, true);
  }
};

export const tipSpeed = (
  unitInfo: UnitInfoImpactCrusherCIInterface | UnitInfoCrusherVSIInterface,
  textArray: UnitInfoTextInterface[],
) => {
  addValue(textArray)(
    `Tip speed: ${unitInfo.tipSpeed} m/s`,
    unitInfo.display.displayTipSpeed,
  );
};

export const liveBinDisplay = (
  unitInfo: UnitInfoCrusherVSIInterface | UnitInfoCrusherInterface,
  liveBin: number | undefined,
  textArray: UnitInfoTextInterface[],
) => {
  const metricType = store.getState().settingsState.metricType;
  if (typeof liveBin === 'undefined') {
    return;
  }
  const text =
    unitInfo.parallelUnits > 1
      ? `Minimum live bin volume: ${
          unitInfo.parallelUnits
        } x ${convertToCurrentMetric({
          value: liveBin,
          metricType: metricType,
          conversionType: CONVERSIONS.VOLUME,
        })} ${getMetricUnit(metricType, CONVERSIONS.VOLUME)}`
      : `Minimum live bin volume: ${convertToCurrentMetric({
          value: liveBin,
          metricType: metricType,
          conversionType: CONVERSIONS.VOLUME,
        })} ${getMetricUnit(metricType, CONVERSIONS.VOLUME)}`;

  addValue(textArray)(text, unitInfo.display.displayMinLiveBin);
};

export const css = (
  unitInfo: UnitInfoCrusherInterface | UnitInfoJawCrusherInterface,
  textArray: UnitInfoTextInterface[],
) => {
  addValue(textArray)(
    toMetricRoundConv('CSS:', unitInfo.css),
    unitInfo.display.displayCss,
  );
};

export const productSize = (
  unitInfo: UnitInfoStorageInterface,
  outputFlow: PortOutputFlowInterface,
  textArray: UnitInfoTextInterface[],
  label = '',
) => {
  const metricType = store.getState().settingsState.metricType;
  if (unitInfo.display.displayProductSize) {
    const minSize = outputFlow.lowerFractionSize;
    const maxSize = outputFlow.upperFractionSize;
    const topSize = outputFlow.topSize;
    if (unitInfo.display.displayPredictedOversize) {
      textArray.push({
        text: `${label}${convertToCurrentMetric({
          value: minSize,
          metricType: metricType,
          conversionType: CONVERSIONS.MILLIMETER,
        })} - ${convertToCurrentMetric({
          value: maxSize,
          metricType: metricType,
          conversionType: CONVERSIONS.MILLIMETER,
        })} (${convertToCurrentMetric({
          value: topSize,
          metricType: metricType,
          conversionType: CONVERSIONS.MILLIMETER,
        })}) ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)}`,
      });
    } else {
      textArray.push({
        text: `${label}${convertToCurrentMetric({
          value: minSize,
          metricType: metricType,
          conversionType: CONVERSIONS.MILLIMETER,
        })} - ${convertToCurrentMetric({
          value: maxSize,
          metricType: metricType,
          conversionType: CONVERSIONS.MILLIMETER,
        })} ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)}`,
      });
    }
    if (metricType === MetricTypes.METRIC_ROUND) {
      textArray.push({
        text: `(Round holes)`,
      });
    }
  }
};

export const configuration = (
  unitInfo:
    | UnitInfoMobileHSIInterface
    | UnitInfoMobileJawInterface
    | UnitInfoMobileJawAndConeInterface
    | UnitInfoMobileVSIInterface
    | UnitInfoMobileConeInterface
    | UnitInfoMobileScreenInterface,
  textArray: UnitInfoTextInterface[],
) => {
  const add = addValue(textArray);
  const { displayConfig } = unitInfo.display;
  add('Configuration: ', displayConfig);
  add(unitInfo.configuration.displayName, displayConfig);
};

export const capacityRange = (
  low: number,
  high: number,
  display: boolean,
  textArray: UnitInfoTextInterface[],
) => {
  addValue(textArray)(
    `Capacity range: ${toMetricCapacity('', low, {
      hideUnit: true,
    })} - ${toMetricCapacity('', high)}`,
    display,
  );
};

export const attachmentTool = (
  type: UnitTypes,
  attachmentTool: AttachementTool,
  textArray: UnitInfoTextInterface[],
  indent?: boolean,
) => {
  const { application, boom, breaker, powerPack } = attachmentTool;
  const applicationTexts = {
    [BreakerApplication.GYRATORY]: 'Gyratory crusher',
    [BreakerApplication.JAW_CRUSHER]: 'Jaw crusher',
    [BreakerApplication.STATIONARY_GRIZZLY]: 'Stationary grizzly',
  };

  const add = addValue(textArray);
  const space = indent ? `\u200B    ` : '';
  if (type === UnitTypes.BREAKER)
    add(`${space}Application: ${applicationTexts[application]}`, true);
  add(`${space}Breaker model: ${breaker}`, !!breaker);
  add(`${space}Boom model: ${boom}`, !!boom);
  add(`${space}Power pack: ${powerPack}`, !!powerPack);

  return textArray;
};
